import { ref, onMounted, onUnmounted } from 'vue';

const NAV_SELECTOR = '[data-nav-main]';
const CSS_VAR_NAME = '--nav-offset';

export function useNavOffset() {
   const navOffset = ref(0);
   let resizeObserver: ResizeObserver | null = null;
   let classMutationObserver: MutationObserver | null = null;
   let domMutationObserver: MutationObserver | null = null;
   let navElement: Element | null = null;

   const isNavVisible = (element: Element): boolean => {
      return !element.classList.contains('opacity-0');
   };

   const updateNavOffset = () => {
      if (!navElement) {
         return;
      }

      const visible = isNavVisible(navElement);
      const height = visible ? navElement.getBoundingClientRect().height : 0;

      // Skip if visible nav reports 0 height — layout hasn't completed yet.
      // ResizeObserver will fire once layout completes with the real height.
      if (visible && height === 0) {
         return;
      }

      navOffset.value = height;
      document.documentElement.style.setProperty(CSS_VAR_NAME, `${height}px`);
   };

   const attachObservers = (element: Element) => {
      navElement = element;
      updateNavOffset();

      resizeObserver = new ResizeObserver(() => {
         updateNavOffset();
      });
      resizeObserver.observe(navElement);

      classMutationObserver = new MutationObserver((mutations) => {
         for (const mutation of mutations) {
            if (mutation.attributeName === 'class') {
               updateNavOffset();
               break;
            }
         }
      });
      classMutationObserver.observe(navElement, { attributes: true, attributeFilter: ['class'] });
   };

   const stopDomObserver = () => {
      if (domMutationObserver) {
         domMutationObserver.disconnect();
         domMutationObserver = null;
      }
   };

   const waitForNavElement = () => {
      // Watch the DOM for the nav element to appear (e.g. after Auth0 callback redirect)
      domMutationObserver = new MutationObserver(() => {
         const el = document.querySelector(NAV_SELECTOR);
         if (el) {
            stopDomObserver();
            attachObservers(el);
         }
      });
      domMutationObserver.observe(document.body, { childList: true, subtree: true });
   };

   onMounted(() => {
      const found = document.querySelector(NAV_SELECTOR);
      if (found) {
         attachObservers(found);
         return;
      }

      // Nav may not exist yet (e.g. Auth0 callback shows spinner instead of nav).
      // Watch for it to appear in the DOM.
      waitForNavElement();
   });

   onUnmounted(() => {
      if (resizeObserver) {
         resizeObserver.disconnect();
         resizeObserver = null;
      }
      if (classMutationObserver) {
         classMutationObserver.disconnect();
         classMutationObserver = null;
      }
      stopDomObserver();
      navElement = null;
   });

   return {
      navOffset,
   };
}
